/**
 * Utility functions for proper timezone display and handling
 */

/**
 * Get the correct timezone abbreviation and offset for display.
 * Mountain Time is always displayed as 'MT' (never 'MDT') per CHS conventions.
 * Arizona uses 'MST' (no DST).
 */
export const getTimezoneDisplayInfo = (
  timezone: string,
  ianaTimeZone: string
) => {
  const now = new Date();

  // Get the timezone offset in minutes
  const utcTime = new Date(
    now.getTime() + now.getTimezoneOffset() * 60000
  );
  const targetTime = new Date(
    utcTime.toLocaleString('en-US', { timeZone: ianaTimeZone })
  );
  const offsetMinutes =
    (targetTime.getTime() - utcTime.getTime()) / 60000;

  // Convert offset to hours
  const offsetHours = offsetMinutes / 60;

  const isDST = isDaylightSavingTime(ianaTimeZone);

  let displayAbbreviation = timezone;

  // MT/MST/MDT all map to America/Denver — always show 'MT' regardless of DST.
  if (timezone === 'MT' || timezone === 'MST' || timezone === 'MDT') {
    displayAbbreviation = 'MT';
  // MST_AZ / MST_STATIC are Arizona (no DST) — display as 'MST'.
  } else if (timezone === 'MST_AZ' || timezone === 'MST_STATIC') {
    displayAbbreviation = 'MST';
  } else if (timezone === 'PST' && isDST) {
    displayAbbreviation = 'PDT';
  } else if (timezone === 'CST' && isDST) {
    displayAbbreviation = 'CDT';
  } else if (timezone === 'EST' && isDST) {
    displayAbbreviation = 'EDT';
  }

  return {
    abbreviation: displayAbbreviation,
    offset: offsetHours,
    isDST,
    ianaTimeZone,
  };
};

/**
 * Check if a timezone is currently in daylight saving time
 */
export const isDaylightSavingTime = (
  ianaTimeZone: string
): boolean => {
  const now = new Date();
  
  // Use Intl.DateTimeFormat to get the timezone name
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaTimeZone,
    timeZoneName: 'short'
  });
  
  const parts = formatter.formatToParts(now);
  const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;
  
  // Check if the timezone name contains 'D' (Daylight) instead of 'S' (Standard)
  return timeZoneName?.includes('DT') || timeZoneName?.includes('DST') || false;
};


/**
 * Format time with correct timezone abbreviation
 */
export const formatTimeWithCorrectTimezone = (
  timezone: string,
  ianaTimeZone: string,
  options: Intl.DateTimeFormatOptions = {}
) => {
  const now = new Date();
  const timeInfo = getTimezoneDisplayInfo(timezone, ianaTimeZone);

  const timeString = now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: ianaTimeZone,
    ...options,
  });

  return {
    timeString,
    timezoneAbbreviation: timeInfo.abbreviation,
    isDST: timeInfo.isDST,
  };
};

/**
 * Get the correct timezone abbreviation for display purposes.
 */
export const getCorrectTimezoneAbbreviation = (timezone: string, ianaTimeZone: string): string => {
  const timeInfo = getTimezoneDisplayInfo(timezone, ianaTimeZone);
  return timeInfo.abbreviation;
};
